import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Tabs,
  Card,
  Group,
  Text,
  Badge,
  Button,
  ActionIcon,
  Progress,
  Table,
  Modal,
  TextInput,
  Select,
  NumberInput,
  Stack,
  Code,
  ScrollArea,
  Tooltip,
  SimpleGrid,
  Paper,
  ThemeIcon,
  Loader,
  Alert,
  Switch,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconScript,
  IconPlayerPlay,
  IconPlayerStop,
  IconPlus,
  IconTrash,
  IconClock,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconTerminal2,
  IconRefresh,
  IconKey,
  IconRouter,
  IconEdit,
} from '@tabler/icons-react';
import Editor from '@monaco-editor/react';
import { DeviceCredentialsModal } from '@/components/devices/DeviceCredentialsModal';
import {
  useScanScripts,
  useScanScript,
  useScanJobs,
  useCreateScript,
  useUpdateScript,
  useDeleteScript,
  useCreateJob,
  useCancelJob,
  useScanJobResults,
  useDevices,
  useRelayAgents,
  useCreateRelayTask,
  type ScanScript,
  type ScanJob,
  type ScanResult,
  type RelayAgent,
} from '@/hooks';

export function ScansPage() {
  const [activeTab, setActiveTab] = useState<string | null>('scripts');

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>PowerShell Scanner</Title>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="scripts" leftSection={<IconScript size={16} />}>
            Scripts
          </Tabs.Tab>
          <Tabs.Tab value="jobs" leftSection={<IconTerminal2 size={16} />}>
            Jobs
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="scripts" pt="md">
          <ScriptsTab />
        </Tabs.Panel>

        <Tabs.Panel value="jobs" pt="md">
          <JobsTab />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

// ============ Scripts Tab ============

function ScriptsTab() {
  const { data: scriptsData, isLoading } = useScanScripts();
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [runOpened, { open: openRun, close: closeRun }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [selectedScript, setSelectedScript] = useState<ScanScript | null>(null);

  const handleRunScript = (script: ScanScript) => {
    setSelectedScript(script);
    openRun();
  };

  const handleEditScript = (script: ScanScript) => {
    setSelectedScript(script);
    openEdit();
  };

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  const scripts = scriptsData?.data || [];

  // Group scripts by category
  const scriptsByCategory = scripts.reduce((acc, script) => {
    const cat = script.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(script);
    return acc;
  }, {} as Record<string, ScanScript[]>);

  return (
    <>
      <Group justify="space-between" mb="md">
        <Text c="dimmed">{scripts.length} scripts available</Text>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          New Script
        </Button>
      </Group>

      {scripts.length === 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} title="No Scripts" color="blue">
          No PowerShell scripts found. Create a new script or add built-in scripts.
        </Alert>
      ) : (
        Object.entries(scriptsByCategory).map(([category, categoryScripts]) => (
          <div key={category}>
            <Text fw={600} size="lg" mt="md" mb="sm" tt="capitalize">
              {category}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {categoryScripts.map((script) => (
                <ScriptCard
                  key={script.id}
                  script={script}
                  onRun={() => handleRunScript(script)}
                  onEdit={() => handleEditScript(script)}
                />
              ))}
            </SimpleGrid>
          </div>
        ))
      )}

      <CreateScriptModal opened={createOpened} onClose={closeCreate} />
      {selectedScript && (
        <RunScriptModal
          opened={runOpened}
          onClose={closeRun}
          script={selectedScript}
        />
      )}
      {selectedScript && (
        <EditScriptModal
          opened={editOpened}
          onClose={closeEdit}
          script={selectedScript}
        />
      )}
    </>
  );
}

function ScriptCard({ script, onRun, onEdit }: { script: ScanScript; onRun: () => void; onEdit: () => void }) {
  const deleteScript = useDeleteScript();

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue">
            <IconScript size={14} />
          </ThemeIcon>
          <Text fw={500}>{script.name}</Text>
        </Group>
        {script.isBuiltIn && (
          <Badge size="xs" variant="light">
            Built-in
          </Badge>
        )}
      </Group>

      <Text size="sm" c="dimmed" lineClamp={2} mb="md">
        {script.description || 'No description'}
      </Text>

      <Group gap="xs" mb="md">
        <Badge size="xs" variant="outline">
          {script.outputFormat}
        </Badge>
        <Badge size="xs" variant="outline">
          {script.timeoutSeconds}s timeout
        </Badge>
      </Group>

      <Group justify="space-between">
        <Button
          size="xs"
          leftSection={<IconPlayerPlay size={14} />}
          onClick={onRun}
          disabled={!script.isEnabled}
        >
          Run
        </Button>
        <Group gap="xs">
          <Tooltip label="Edit">
            <ActionIcon
              variant="subtle"
              color="blue"
              size="sm"
              onClick={onEdit}
            >
              <IconEdit size={14} />
            </ActionIcon>
          </Tooltip>
          {!script.isBuiltIn && (
            <Tooltip label="Delete">
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => deleteScript.mutate(script.id)}
                loading={deleteScript.isPending}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
    </Card>
  );
}

// ============ Jobs Tab ============

function JobsTab() {
  const { data: jobsData, isLoading, refetch } = useScanJobs(50);
  const [selectedJob, setSelectedJob] = useState<ScanJob | null>(null);
  const [resultsOpened, { open: openResults, close: closeResults }] = useDisclosure(false);

  const handleViewResults = (job: ScanJob) => {
    setSelectedJob(job);
    openResults();
  };

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  const jobs = jobsData?.data || [];
  const summary = jobsData?.summary;

  return (
    <>
      <Group justify="space-between" mb="md">
        <Group gap="md">
          {summary && (
            <>
              <Badge color="gray">Pending: {summary.pending}</Badge>
              <Badge color="blue">Running: {summary.running}</Badge>
              <Badge color="green">Completed: {summary.completed}</Badge>
              <Badge color="red">Failed: {summary.failed}</Badge>
            </>
          )}
        </Group>
        <Button
          variant="subtle"
          leftSection={<IconRefresh size={16} />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </Group>

      {jobs.length === 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} title="No Jobs" color="blue">
          No scan jobs found. Run a script from the Scripts tab to create a job.
        </Alert>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Script</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Progress</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {jobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                onViewResults={() => handleViewResults(job)}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      {selectedJob && (
        <JobResultsModal
          opened={resultsOpened}
          onClose={closeResults}
          job={selectedJob}
        />
      )}
    </>
  );
}

function JobRow({ job, onViewResults }: { job: ScanJob; onViewResults: () => void }) {
  const cancelJob = useCancelJob();

  const statusColor = {
    pending: 'gray',
    running: 'blue',
    completed: 'green',
    failed: 'red',
    cancelled: 'orange',
  }[job.status];

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" fw={500}>
          {job.name || 'Unnamed Job'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light">{job.script.name}</Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={statusColor}>{job.statusLabel}</Badge>
      </Table.Td>
      <Table.Td style={{ width: 200 }}>
        <Group gap="xs">
          <Progress
            value={job.progressPercent}
            size="sm"
            style={{ flex: 1 }}
            color={job.devicesFailed > 0 ? 'red' : 'blue'}
          />
          <Text size="xs" c="dimmed">
            {job.devicesCompleted + job.devicesFailed + job.devicesSkipped}/{job.devicesTotal}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {new Date(job.createdAt).toLocaleString()}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Tooltip label="View Results">
            <ActionIcon variant="subtle" onClick={onViewResults}>
              <IconTerminal2 size={16} />
            </ActionIcon>
          </Tooltip>
          {job.status === 'running' && (
            <Tooltip label="Cancel">
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => cancelJob.mutate(job.id)}
                loading={cancelJob.isPending}
              >
                <IconPlayerStop size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

// ============ Modals ============

function CreateScriptModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const createScript = useCreateScript();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('custom');
  const [scriptContent, setScriptContent] = useState('');
  const [timeoutSeconds, setTimeoutSeconds] = useState(300);

  const handleSubmit = () => {
    createScript.mutate(
      {
        name,
        description,
        category,
        scriptContent,
        timeoutSeconds,
        outputFormat: 'json',
      },
      {
        onSuccess: () => {
          onClose();
          setName('');
          setDescription('');
          setScriptContent('');
        },
      }
    );
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Create PowerShell Script" size="xl">
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Script name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextInput
          label="Description"
          placeholder="What does this script do?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Select
          label="Category"
          value={category}
          onChange={(v) => setCategory(v || 'custom')}
          data={[
            { value: 'inventory', label: 'Inventory' },
            { value: 'security', label: 'Security' },
            { value: 'maintenance', label: 'Maintenance' },
            { value: 'custom', label: 'Custom' },
          ]}
        />
        <div>
          <Text size="sm" fw={500} mb="xs">PowerShell Script</Text>
          <div style={{ border: '1px solid var(--mantine-color-dark-4)', borderRadius: 4 }}>
            <Editor
              height="300px"
              defaultLanguage="powershell"
              value={scriptContent}
              onChange={(value) => setScriptContent(value || '')}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                automaticLayout: true,
                tabSize: 4,
              }}
            />
          </div>
        </div>
        <NumberInput
          label="Timeout (seconds)"
          value={timeoutSeconds}
          onChange={(v) => setTimeoutSeconds(Number(v) || 300)}
          min={10}
          max={3600}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={createScript.isPending}
            disabled={!name || !scriptContent}
          >
            Create Script
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function EditScriptModal({
  opened,
  onClose,
  script,
}: {
  opened: boolean;
  onClose: () => void;
  script: ScanScript;
}) {
  const { data: fullScript, isLoading } = useScanScript(script.id);
  const updateScript = useUpdateScript();
  const [name, setName] = useState(script.name);
  const [description, setDescription] = useState(script.description || '');
  const [category, setCategory] = useState(script.category);
  const [scriptContent, setScriptContent] = useState('');
  const [timeoutSeconds, setTimeoutSeconds] = useState(script.timeoutSeconds);
  const [isEnabled, setIsEnabled] = useState(script.isEnabled);

  // Load script content when fullScript is fetched
  useEffect(() => {
    if (fullScript?.scriptContent) {
      setScriptContent(fullScript.scriptContent);
    }
  }, [fullScript]);

  // Reset form when script changes
  useEffect(() => {
    setName(script.name);
    setDescription(script.description || '');
    setCategory(script.category);
    setTimeoutSeconds(script.timeoutSeconds);
    setIsEnabled(script.isEnabled);
  }, [script]);

  const handleSubmit = () => {
    updateScript.mutate(
      {
        id: script.id,
        name,
        description,
        category,
        scriptContent,
        timeoutSeconds,
        isEnabled,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Edit Script: ${script.name}`} size="xl">
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Script name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextInput
          label="Description"
          placeholder="What does this script do?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Group grow>
          <Select
            label="Category"
            value={category}
            onChange={(v) => setCategory(v || 'custom')}
            data={[
              { value: 'inventory', label: 'Inventory' },
              { value: 'security', label: 'Security' },
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'custom', label: 'Custom' },
            ]}
          />
          <NumberInput
            label="Timeout (seconds)"
            value={timeoutSeconds}
            onChange={(v) => setTimeoutSeconds(Number(v) || 300)}
            min={10}
            max={3600}
          />
        </Group>
        <Switch
          label="Enabled"
          checked={isEnabled}
          onChange={(e) => setIsEnabled(e.currentTarget.checked)}
        />
        <div>
          <Text size="sm" fw={500} mb="xs">PowerShell Script</Text>
          {isLoading ? (
            <Group justify="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Loading script...</Text>
            </Group>
          ) : (
            <div style={{ border: '1px solid var(--mantine-color-dark-4)', borderRadius: 4 }}>
              <Editor
                height="400px"
                defaultLanguage="powershell"
                value={scriptContent}
                onChange={(value) => setScriptContent(value || '')}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  automaticLayout: true,
                  tabSize: 4,
                }}
              />
            </div>
          )}
        </div>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={updateScript.isPending}
            disabled={!name || !scriptContent || isLoading}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RunScriptModal({
  opened,
  onClose,
  script,
}: {
  opened: boolean;
  onClose: () => void;
  script: ScanScript;
}) {
  const { data: devicesData, isLoading: devicesLoading, error: devicesError } = useDevices({ page: 1, limit: 100 });
  const { data: agents } = useRelayAgents();
  const { data: fullScript, isLoading: isLoadingScript } = useScanScript(script.id);  // Fetch full script with content
  const createJob = useCreateJob();
  const createRelayTask = useCreateRelayTask();
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [credentialsOpened, { open: openCredentials, close: closeCredentials }] = useDisclosure(false);
  const [credentialDevice, setCredentialDevice] = useState<{ id: string; hostname: string | null } | null>(null);
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [useRelayAgent, setUseRelayAgent] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [targetIp, setTargetIp] = useState('');
  const [targetCredentials, setTargetCredentials] = useState({ username: '', password: '', domain: '' });
  const [protocol, setProtocol] = useState<string>('winrm');

  const devices = devicesData?.devices || [];

  // Get script parameters (if any)
  const scriptParams = script.parameters || {};
  const hasParams = Object.keys(scriptParams).length > 0;

  // Initialize parameters when script changes
  useEffect(() => {
    if (hasParams) {
      const initialParams: Record<string, string> = {};
      Object.entries(scriptParams).forEach(([key, defaultValue]) => {
        initialParams[key] = String(defaultValue || '');
      });
      setParameters(initialParams);
    }
  }, [script.id, hasParams, scriptParams]);

  const handleRun = () => {
    if (useRelayAgent) {
      // Run via relay agent
      if (!selectedAgent || !targetIp) return;

      // Debug logging
      console.log('=== Script Debug ===');
      console.log('script.id:', script.id);
      console.log('script.scriptContent:', script.scriptContent);
      console.log('fullScript:', fullScript);
      console.log('fullScript?.scriptContent:', fullScript?.scriptContent);
      console.log('isLoadingScript:', isLoadingScript);

      // Build the script content with parameters (use fullScript which has the content)
      let scriptContent = fullScript?.scriptContent || script.scriptContent || '';

      console.log('Final scriptContent length:', scriptContent.length);

      // Safety check - don't run if script content is empty
      if (!scriptContent || scriptContent.trim() === '') {
        notifications.show({
          title: 'Error',
          message: 'Script content is empty. Cannot run script.',
          color: 'red',
        });
        return;
      }

      if (hasParams && scriptContent) {
        Object.entries(parameters).forEach(([key, value]) => {
          scriptContent = scriptContent.replace(new RegExp(`\\$${key}\\s*=\\s*['"][^'"]*['"]`, 'g'), `$${key} = '${value}'`);
        });
      }

      createRelayTask.mutate(
        {
          type: 'scan_script',
          agent_id: selectedAgent,
          payload: {
            device_id: `relay-${targetIp.replace(/\./g, '-')}`,
            device_ip: targetIp,
            protocol: protocol,
            script_content: scriptContent,
            username: targetCredentials.username,
            password: targetCredentials.password,
            domain: protocol === 'winrm' ? (targetCredentials.domain || undefined) : undefined,
            timeout: script.timeoutSeconds || 60,
          },
        },
        {
          onSuccess: () => {
            onClose();
            setSelectedAgent(null);
            setTargetIp('');
            setTargetCredentials({ username: '', password: '', domain: '' });
            setProtocol('winrm');
            setParameters({});
          },
        }
      );
    } else {
      // Run directly via server
      if (selectedDevices.length === 0) return;

      createJob.mutate(
        {
          scriptId: script.id,
          deviceIds: selectedDevices,
          createdBy: 'ui',
          autoStart: true,
          checkOnlineStatus: true,
          parameters: hasParams ? parameters : undefined,
        },
        {
          onSuccess: () => {
            onClose();
            setSelectedDevices([]);
            setParameters({});
          },
        }
      );
    }
  };

  const handleSetCredentials = () => {
    if (selectedDevices.length === 0) return;
    const device = devices.find((d) => d.id === selectedDevices[0]);
    if (device) {
      setCredentialDevice({ id: device.id, hostname: device.hostname });
      openCredentials();
    }
  };

  const isRunDisabled = useRelayAgent
    ? !selectedAgent || !targetIp || !targetCredentials.username || !targetCredentials.password || isLoadingScript || !fullScript?.scriptContent
    : selectedDevices.length === 0;

  return (
    <>
      <Modal opened={opened} onClose={onClose} title={`Run: ${script.name}`} size="lg">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {script.description}
          </Text>

          {/* Relay Agent Toggle */}
          <Switch
            label={
              <Group gap="xs">
                <IconRouter size={16} />
                <Text size="sm">Run via Relay Agent</Text>
              </Group>
            }
            description="Use a relay agent to reach devices on remote networks"
            checked={useRelayAgent}
            onChange={(e) => setUseRelayAgent(e.currentTarget.checked)}
          />

          {useRelayAgent ? (
            <>
              {/* Relay Agent Selection */}
              <Select
                label="Relay Agent"
                placeholder="Select agent to execute script"
                data={
                  agents?.map((agent: RelayAgent) => ({
                    value: agent.id,
                    label: `${agent.name} (${agent.status})`,
                  })) || []
                }
                value={selectedAgent}
                onChange={setSelectedAgent}
                leftSection={<IconRouter size={16} />}
              />

              {/* Protocol Selection */}
              <Select
                label="Protocol"
                description="WinRM for Windows, SSH for Linux"
                data={[
                  { value: 'winrm', label: 'WinRM (Windows)' },
                  { value: 'ssh', label: 'SSH (Linux)' },
                ]}
                value={protocol}
                onChange={(v) => setProtocol(v || 'winrm')}
              />

              {/* Target Device - select from list or enter IP manually */}
              <Select
                label="Select Device (optional)"
                placeholder="Choose a device or enter IP manually below"
                data={devices.map((d) => ({
                  value: d.id,
                  label: `${d.hostname || d.id}${d.ipAddress ? ` (${d.ipAddress})` : ''}`,
                }))}
                value={selectedDevices[0] || null}
                onChange={(v) => {
                  setSelectedDevices(v ? [v] : []);
                  if (v) {
                    const device = devices.find((d) => d.id === v);
                    if (device?.ipAddress) {
                      setTargetIp(device.ipAddress);
                    }
                  }
                }}
                searchable
                clearable
                disabled={devicesLoading || devices.length === 0}
              />

              <TextInput
                label="Target Device IP"
                placeholder="Enter IP address (e.g., 192.168.1.100)"
                description={`Enter any IP address to connect to via ${protocol.toUpperCase()}`}
                value={targetIp}
                onChange={(e) => setTargetIp(e.target.value)}
                required
              />

              {/* Credentials for target device */}
              <Text size="sm" fw={500}>Target Device Credentials</Text>
              <Group grow>
                <TextInput
                  label="Username"
                  placeholder={protocol === 'ssh' ? 'root' : 'Administrator'}
                  value={targetCredentials.username}
                  onChange={(e) => setTargetCredentials({ ...targetCredentials, username: e.target.value })}
                  required
                />
                <TextInput
                  label="Password"
                  type="password"
                  placeholder="Password"
                  value={targetCredentials.password}
                  onChange={(e) => setTargetCredentials({ ...targetCredentials, password: e.target.value })}
                  required
                />
              </Group>
              {protocol === 'winrm' && (
                <TextInput
                  label="Domain (optional)"
                  placeholder="DOMAIN"
                  value={targetCredentials.domain}
                  onChange={(e) => setTargetCredentials({ ...targetCredentials, domain: e.target.value })}
                />
              )}
            </>
          ) : (
            <>
              {devicesLoading && <Text size="sm">Loading devices...</Text>}
              {devicesError && <Text size="sm" c="red">Error loading devices: {String(devicesError)}</Text>}
              {!devicesLoading && devices.length === 0 && <Text size="sm" c="orange">No devices found</Text>}

              <Select
                label={`Select Devices (${devices.length} available)`}
                placeholder={devicesLoading ? "Loading..." : "Choose devices to run script on"}
                data={devices.map((d) => ({
                  value: d.id,
                  label: d.hostname || d.id,
                }))}
                value={selectedDevices[0] || null}
                onChange={(v) => setSelectedDevices(v ? [v] : [])}
                searchable
                clearable
                disabled={devicesLoading || devices.length === 0}
                comboboxProps={{ withinPortal: true, zIndex: 1000 }}
              />

              {selectedDevices.length > 0 && (
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconKey size={14} />}
                  onClick={handleSetCredentials}
                >
                  Set WinRM Credentials
                </Button>
              )}
            </>
          )}

          {hasParams && (
            <>
              <Text size="sm" fw={500} mt="sm">Parameters</Text>
              {Object.keys(scriptParams).map((paramName) => (
                <TextInput
                  key={paramName}
                  label={paramName}
                  placeholder={`Enter ${paramName}`}
                  value={parameters[paramName] || ''}
                  onChange={(e) => setParameters({ ...parameters, [paramName]: e.target.value })}
                  description={
                    paramName === 'NewHostname' ? 'Max 15 characters (letters, numbers, hyphens)' :
                    paramName === 'Reboot' ? 'Set to "true" to reboot immediately' :
                    paramName === 'PackageName' ? 'e.g., googlechrome, firefox, 7zip, vlc' :
                    paramName === 'Packages' ? 'Comma-separated list, e.g., 7zip,vlc,firefox' :
                    paramName === 'ImagePath' ? 'URL or network path (\\\\server\\share\\image.jpg)' :
                    paramName === 'SoftwareName' ? 'Name of software to uninstall' :
                    undefined
                  }
                />
              ))}
            </>
          )}

          {useRelayAgent && isLoadingScript && (
            <Alert color="blue" variant="light">
              Loading script content...
            </Alert>
          )}

          {useRelayAgent && !isLoadingScript && !fullScript?.scriptContent && (
            <Alert color="red" variant="light">
              Script content is empty or could not be loaded!
            </Alert>
          )}

          <Text size="xs" c="dimmed">
            {useRelayAgent
              ? `The relay agent will connect to the target device via ${protocol.toUpperCase()} and execute the script.`
              : 'Script will only run on online devices with WinRM enabled. Set device-specific credentials if the default credentials don\'t work.'}
          </Text>

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              onClick={handleRun}
              loading={createJob.isPending || createRelayTask.isPending}
              disabled={isRunDisabled}
            >
              Run Script
            </Button>
          </Group>
        </Stack>
      </Modal>

      <DeviceCredentialsModal
        opened={credentialsOpened}
        onClose={closeCredentials}
        device={credentialDevice}
      />
    </>
  );
}

function JobResultsModal({
  opened,
  onClose,
  job,
}: {
  opened: boolean;
  onClose: () => void;
  job: ScanJob;
}) {
  const { data: resultsData, isLoading } = useScanJobResults(job.id);

  const results = resultsData?.data || [];
  const summary = resultsData?.summary;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Results: ${job.name || 'Unnamed Job'}`}
      size="xl"
    >
      <Stack gap="md">
        <Group gap="md">
          {summary && (
            <>
              <Badge color="green" leftSection={<IconCheck size={12} />}>
                Success: {summary.success}
              </Badge>
              <Badge color="red" leftSection={<IconX size={12} />}>
                Failed: {summary.failed}
              </Badge>
              <Badge color="orange" leftSection={<IconClock size={12} />}>
                Timeout: {summary.timeout}
              </Badge>
              <Badge color="gray">Skipped: {summary.skipped}</Badge>
            </>
          )}
        </Group>

        {isLoading ? (
          <Group justify="center" py="md">
            <Loader />
          </Group>
        ) : results.length === 0 ? (
          <Text c="dimmed">No results yet.</Text>
        ) : (
          <ScrollArea h={400}>
            <Stack gap="sm">
              {results.map((result) => (
                <ResultCard key={result.id} result={result} />
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Stack>
    </Modal>
  );
}

function ResultCard({ result }: { result: ScanResult }) {
  const [expanded, setExpanded] = useState(false);

  const statusIcon = {
    success: <IconCheck size={14} />,
    failed: <IconX size={14} />,
    timeout: <IconClock size={14} />,
    skipped: <IconAlertCircle size={14} />,
  }[result.status];

  const statusColor = {
    success: 'green',
    failed: 'red',
    timeout: 'orange',
    skipped: 'gray',
  }[result.status];

  return (
    <Paper p="sm" withBorder>
      <Group justify="space-between" mb={expanded ? 'sm' : 0}>
        <Group gap="sm">
          <ThemeIcon size="sm" color={statusColor} variant="light">
            {statusIcon}
          </ThemeIcon>
          <Text fw={500}>{result.device.hostname || result.device.id}</Text>
          <Badge size="xs" color={statusColor}>
            {result.statusLabel}
          </Badge>
          {result.executionTimeMs && (
            <Text size="xs" c="dimmed">
              {result.executionTimeMs}ms
            </Text>
          )}
        </Group>
        <Button
          variant="subtle"
          size="xs"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide' : 'Show'} Output
        </Button>
      </Group>

      {expanded && (
        <Stack gap="xs" mt="sm">
          {result.outputData !== null && result.outputData !== undefined && (
            <>
              <Text size="xs" fw={500}>
                Output Data:
              </Text>
              <Code block style={{ maxHeight: 200, overflow: 'auto' }}>
                {JSON.stringify(result.outputData, null, 2)}
              </Code>
            </>
          )}
          {result.rawOutput && (
            <>
              <Text size="xs" fw={500}>
                Raw Output:
              </Text>
              <Code block style={{ maxHeight: 200, overflow: 'auto' }}>
                {result.rawOutput}
              </Code>
            </>
          )}
          {result.errorOutput && (
            <>
              <Text size="xs" fw={500} c="red">
                Error:
              </Text>
              <Code block color="red" style={{ maxHeight: 200, overflow: 'auto' }}>
                {result.errorOutput}
              </Code>
            </>
          )}
        </Stack>
      )}
    </Paper>
  );
}
